import React, { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Box, IconButton, Typography, Stack, Tooltip } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowBackIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import RefreshIcon from "@mui/icons-material/RefreshOutlined";
import Sidebar from "../components/common/Sidebar";

const DRAWER_WIDTH = 240;
const COLLAPSED_WIDTH = 72;
const STORAGE_KEY = "wa.sidebar.collapsed";

// Derive a friendly title for the mobile header from the current URL.
const ROUTE_LABELS = {
  search: "Search",
  dashboard: "Dashboard",
  companies: "Companies",
  details: "Details",
  users: "Users",
  devices: "Devices",
  tickets: "Tickets",
  offline: "Offline Companies",
  "audit-log": "Audit Log",
  profile: "Profile",
};

const labelFor = (segment) =>
  ROUTE_LABELS[segment] ||
  segment
    .split("-")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");

const buildCrumbs = (pathname) => {
  const parts = pathname.split("/").filter(Boolean);
  const crumbs = [];
  // skip ObjectId-like segments (Mongo ids)
  for (const p of parts) {
    if (/^[0-9a-fA-F]{24}$/.test(p)) continue;
    crumbs.push(labelFor(p));
  }
  return crumbs.length ? crumbs : ["Home"];
};

export default function MainLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const handleDrawerToggle = () => setMobileOpen((p) => !p);
  const handleCollapseToggle = () =>
    setDesktopCollapsed((p) => {
      const next = !p;
      try {
        localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* noop */
      }
      return next;
    });

  const drawerWidth = desktopCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  return (
    <Box
      sx={{
        display: "flex",
        // Mobile: natural document height so window scroll works (needed
        // for infinite-scroll IntersectionObserver). Desktop: viewport-locked.
        height: { xs: "auto", md: "100vh" },
        minHeight: { xs: "100vh", md: "100vh" },
        maxHeight: { md: "100vh" },
        overflow: { xs: "visible", md: "hidden" },
        backgroundColor: "background.default",
      }}
    >
      <Sidebar
        drawerWidth={drawerWidth}
        expandedWidth={DRAWER_WIDTH}
        mobileOpen={mobileOpen}
        onClose={handleDrawerToggle}
        collapsed={desktopCollapsed}
        onToggleCollapse={handleCollapseToggle}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          display: "flex",
          flexDirection: "column",
          // Mobile: natural height (document scrolls). Desktop: locked to vh.
          height: { md: "100vh" },
          maxHeight: { md: "100vh" },
          overflow: { xs: "visible", md: "hidden" },
          transition: (t) =>
            t.transitions.create(["width", "margin"], {
              easing: t.transitions.easing.sharp,
              duration: t.transitions.duration.shorter,
            }),
        }}
      >
        {/* Mobile header strip — hamburger + back + dynamic breadcrumb.
            Sticky at top so it stays visible while the page scrolls.
            Hidden on md+ since desktop uses the per-page breadcrumb instead. */}
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            alignItems: "center",
            gap: 0.75,
            px: 1,
            height: 48,
            flexShrink: 0,
            backgroundColor: "background.paper",
            borderBottom: "1px solid",
            borderColor: "divider",
            position: "sticky",
            top: 0,
            zIndex: (t) => t.zIndex.appBar,
          }}
        >
          <IconButton
            onClick={handleDrawerToggle}
            size="small"
            sx={{
              backgroundColor: "primary.light",
              color: "primary.main",
              "&:hover": {
                backgroundColor: "primary.main",
                color: "common.white",
              },
            }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>
          <Tooltip title="Back">
            <IconButton
              onClick={() => navigate(-1)}
              size="small"
              sx={{
                backgroundColor: "primary.light",
                color: "primary.main",
                "&:hover": {
                  backgroundColor: "primary.main",
                  color: "common.white",
                },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ minWidth: 0, flex: 1, ml: 0.5 }}
          >
            {buildCrumbs(location.pathname).map((crumb, i, arr) => (
              <React.Fragment key={`${crumb}-${i}`}>
                <Typography
                  noWrap
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: i === arr.length - 1 ? 700 : 500,
                    color:
                      i === arr.length - 1
                        ? "primary.dark"
                        : "primary.main",
                  }}
                >
                  {crumb}
                </Typography>
                {i < arr.length - 1 && (
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      color: "primary.main",
                      opacity: 0.6,
                      lineHeight: 1,
                    }}
                  >
                    ›
                  </Typography>
                )}
              </React.Fragment>
            ))}
          </Stack>
          <Box
            component="button"
            type="button"
            onClick={() => window.location.reload()}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              flexShrink: 0,
              border: "1px solid",
              borderColor: "primary.main",
              borderRadius: "6px",
              backgroundColor: "primary.light",
              color: "primary.main",
              fontSize: "0.75rem",
              fontWeight: 700,
              px: 0.75,
              py: 0.5,
              cursor: "pointer",
              fontFamily: "inherit",
              transition: "background-color .15s, color .15s",
              "&:hover": {
                backgroundColor: "primary.main",
                color: "common.white",
                borderColor: "primary.main",
              },
            }}
          >
            <RefreshIcon sx={{ fontSize: 16 }} />
            Refresh
          </Box>
        </Box>

        <Box
          sx={{
            pt: { xs: 1.5, md: 2 },
            pl: { xs: 1.5, sm: 2, md: 2 },
            pr: { xs: 1.5, sm: 2, md: 2 },
            pb: { xs: 1.5, sm: 2, md: 2 },
            flex: { md: 1 },
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            // Desktop: scroll within the content area when pages are taller
            // than the viewport (e.g. Company Details form). Pages that prefer
            // an internal-only scroll (Companies list table) handle it via
            // their own flex children with overflow: auto.
            overflowY: { xs: "visible", md: "auto" },
            overflowX: "hidden",
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}

